import { assistanceApi, describeAssistanceFailure, toError } from '@/modules/ai';
import type { AttributeDefinition, Category } from '@/api/schemas/catalog-taxonomy';
import type {
  AttributeDraft,
  CategoryDraft,
  CreatedCategory,
  SetupPatch,
} from '@/modules/catalog/pages/setup-page/types';
import {
  buildAttributeDrafts,
  buildCategoryDrafts,
} from '@/modules/catalog/pages/setup-page/parts/drafts';
import { saveAttributeDraft, saveCategoryDraft } from '@/modules/catalog/pages/setup-page/parts/save';
import type { AttributeSaveResult } from '@/modules/catalog/pages/setup-page/parts/save';
import type { ManualAttributeInput, ManualCategoryInput } from '@/modules/catalog/pages/setup-page/types';

/**
 * WHAT THE PAGE DOES, AS ACTIONS.
 *
 * ---------------------------------------------------------------------------
 * THE ACTIONS ARE FUNCTIONS, NOT HOOK BODIES
 * ---------------------------------------------------------------------------
 * Each of these is a plain function that takes the state it needs and writes
 * through the page's one updater. Keeping them out of a component's body is
 * what keeps that component readable, and it makes each step testable on its
 * own: a refusal in one item never becomes an exception that swallows the
 * others.
 */

/** Asks the assistant for a category tree. */
export async function runSuggestCategories(params: {
  readonly instructions: string;
  readonly existingCategories: ReadonlyArray<Category>;
  readonly patch: SetupPatch;
}): Promise<void> {
  params.patch((prev) => ({ ...prev, isWorking: true, categoryFailure: null }));
  try {
    const proposal = await assistanceApi.suggestCategories({
      instructions: params.instructions,
      existingCategories: params.existingCategories.map((category) => ({
        id: category.categoryId,
        name: category.name,
      })),
    });
    params.patch((prev) => ({
      ...prev,
      categoryProposal: proposal,
      categoryDrafts: buildCategoryDrafts(proposal),
    }));
  } catch (cause) {
    params.patch((prev) => ({ ...prev, categoryFailure: describeAssistanceFailure(toError(cause)) }));
  } finally {
    params.patch((prev) => ({ ...prev, isWorking: false }));
  }
}

/** Asks the assistant for the attributes of one category. */
export async function runSuggestAttributes(params: {
  readonly instructions: string;
  readonly targetCategoryId: string;
  readonly existingAttributes: ReadonlyArray<AttributeDefinition>;
  readonly patch: SetupPatch;
}): Promise<void> {
  params.patch((prev) => ({ ...prev, isWorking: true, attributeFailure: null }));
  try {
    const proposal = await assistanceApi.suggestAttributes({
      instructions: params.instructions,
      ...(params.targetCategoryId === '' ? {} : { categoryId: params.targetCategoryId }),
      existingAttributes: params.existingAttributes.map((definition) => ({
        // The dictionary has no separate identifier: the tenant-unique KEY is
        // the record's identity, and it is what the model needs to reuse it.
        id: definition.attributeKey,
        key: definition.attributeKey,
        name: definition.name,
        kind: definition.kind,
      })),
    });
    params.patch((prev) => ({
      ...prev,
      attributeProposal: proposal,
      attributeDrafts: buildAttributeDrafts(proposal),
    }));
  } catch (cause) {
    params.patch((prev) => ({
      ...prev,
      attributeFailure: describeAssistanceFailure(toError(cause)),
    }));
  } finally {
    params.patch((prev) => ({ ...prev, isWorking: false }));
  }
}

/** Saves every accepted category, one request per item. */
export async function runCreateCategories(params: {
  readonly drafts: ReadonlyArray<CategoryDraft>;
  readonly patch: SetupPatch;
  readonly onSaved: () => void;
}): Promise<void> {
  const accepted = params.drafts.filter((draft) => draft.accepted);
  if (accepted.length === 0) return;

  params.patch((prev) => ({ ...prev, isWorking: true }));
  for (const draft of accepted) {
    const result = await saveCategoryDraft(draft);
    params.patch((prev) => ({
      ...prev,
      categoryDrafts: prev.categoryDrafts.map((row) =>
        row.id === draft.id ? { ...row, outcome: result.outcome } : row,
      ),
      createdCategories:
        result.created === null ? prev.createdCategories : [...prev.createdCategories, result.created],
    }));
  }
  params.patch((prev) => ({ ...prev, isWorking: false }));
  params.onSaved();
}

/** Saves every accepted attribute, one request per item. */
export async function runCreateAttributes(params: {
  readonly drafts: ReadonlyArray<AttributeDraft>;
  readonly target: CreatedCategory | null;
  readonly targetLabel: string | null;
  readonly patch: SetupPatch;
}): Promise<void> {
  const accepted = params.drafts.filter((draft) => draft.accepted);
  if (accepted.length === 0) return;

  params.patch((prev) => ({ ...prev, isWorking: true }));
  for (const draft of accepted) {
    const result = await saveAttributeDraft(draft, params.target, params.targetLabel);
    params.patch((prev) => ({
      ...prev,
      attributeDrafts: prev.attributeDrafts.map((row) =>
        row.id === draft.id ? { ...row, outcome: result.outcome } : row,
      ),
      createdCategories: applyAttachment(prev.createdCategories, params.target, result),
    }));
  }
  params.patch((prev) => ({ ...prev, isWorking: false }));
}

/** Saves the category the merchant typed by hand. */
export async function runCreateManualCategory(params: {
  readonly input: ManualCategoryInput;
  readonly patch: SetupPatch;
  readonly onSaved: () => void;
}): Promise<void> {
  params.patch((prev) => ({ ...prev, isWorking: true, manualCategoryOutcome: null }));
  const result = await saveCategoryDraft({
    id: 'manual',
    idempotencyKey: params.input.idempotencyKey,
    name: params.input.name,
    slug: params.input.slug,
    description: params.input.description,
    parentCategoryId: '',
    parentProposedName: '',
    uncertain: false,
    accepted: true,
    outcome: null,
  });
  params.patch((prev) => ({
    ...prev,
    manualCategoryOutcome: result.outcome,
    createdCategories:
      result.created === null ? prev.createdCategories : [...prev.createdCategories, result.created],
  }));
  params.patch((prev) => ({ ...prev, isWorking: false }));
  params.onSaved();
}

/** Saves the attribute the merchant typed by hand. */
export async function runCreateManualAttribute(params: {
  readonly input: ManualAttributeInput;
  readonly target: CreatedCategory | null;
  readonly targetLabel: string | null;
  readonly patch: SetupPatch;
}): Promise<void> {
  params.patch((prev) => ({ ...prev, isWorking: true, manualAttributeOutcome: null }));
  const result = await saveAttributeDraft(
    {
      id: 'manual',
      idempotencyKey: params.input.idempotencyKey,
      key: params.input.key,
      name: params.input.name,
      kind: params.input.kind,
      description: '',
      variantAxis: 'none',
      optionsText: params.input.options.join(', '),
      assignToCategoryIds: [],
      uncertain: false,
      accepted: true,
      outcome: null,
    },
    params.target,
    params.targetLabel,
  );
  params.patch((prev) => ({
    ...prev,
    manualAttributeOutcome: result.outcome,
    createdCategories: applyAttachment(prev.createdCategories, params.target, result),
  }));
  params.patch((prev) => ({ ...prev, isWorking: false }));
}

/** Folds a written attribute set back into the category it was written to. */
function applyAttachment(
  created: ReadonlyArray<CreatedCategory>,
  target: CreatedCategory | null,
  result: AttributeSaveResult,
): ReadonlyArray<CreatedCategory> {
  const assigned = result.assigned;
  const draftVersion = result.draftVersion;
  if (target === null || assigned === null || draftVersion === null) return created;
  return created.map((category) =>
    category.categoryId === target.categoryId
      ? { ...category, assigned, draftVersion }
      : category,
  );
}
